import "./styles.css";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { captureInstallPrompt } from "@/features/pwa/install";
import { isApiError, setCsrfToken } from "@/lib/api/client";
import { keys } from "@/lib/api/queries";
import { applyTheme, currentTheme } from "@/lib/theme";
import { routeTree } from "./routeTree.gen";

applyTheme(currentTheme());
captureInstallPrompt();

/** A request found the session gone (expired, logged out elsewhere, blocked): back to login. */
function onSessionLost(error: unknown): void {
  if (!isApiError(error, "authentication_required") || !queryClient.getQueryData(keys.session)) {
    return;
  }
  setCsrfToken(null);
  queryClient.clear();
  queryClient.setQueryData(keys.session, null);
  void router.navigate({ to: "/login" });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onSessionLost }),
  mutationCache: new MutationCache({ onError: onSessionLost }),
  defaultOptions: {
    // API errors are answers, not glitches: only network failures are worth retrying.
    queries: { retry: (count, error) => !isApiError(error) && count < 2 },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const root = document.getElementById("root");
if (!root) throw new Error("#root is missing");

if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // Only an install-to-home-screen nicety: the site works the same without it.
    });
  });
}

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
